import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer,
  type StandardChartLayerRenderResult
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import { getRenderOption } from '../../../helper/standardChart';

const visInfo: Layer['visInfo'] = {
  id: 'hierarchicalLine',
  name_en: 'Hierarchical Line',
  // name_zh: '分层折线图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: (amount: number) => amount > 1,
    y: (amount: number) => amount > 1,
    z: (amount: number) => amount > 0
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE,
      priority: DEFINE.DATE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: [DEFINE.ARRAY, DEFINE.SINGLE]
    },
    z: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE,
      priority: DEFINE.STR
    }
  }
};

interface BackgroundLayer {
  value: number;
  color: string;
  label?: string;
  labelColor?: string;
}

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string[];
    z: string;
  };
  /** 背景分层配置 */
  dvBackgroundLayers?: BackgroundLayer[];
}

// 生成背景层markArea数据
function generateMarkAreaData(layers: BackgroundLayer[], top: number) {
  const sortedLayers = [...layers].sort((a, b) => b.value - a.value);
  return sortedLayers.map((layer, index) => {
    const nextLayer = sortedLayers[index + 1];
    const isFirst = index === 0;
    const isLast = index === sortedLayers.length - 1;
    const labelColor = layer.labelColor || layer.color;
    return [
      {
        ...(isFirst ? { y: top } : { yAxis: layer.value }),
        itemStyle: {
          color: layer.color,
          opacity: 0.5
        },
        label: {
          show: !!layer.label,
          color: labelColor,
          position: 'insideTopRight',
          formatter: layer.label
        }
      },
      {
        yAxis: isLast ? 'min' : nextLayer.value
      }
    ];
  });
}

const hierarchicalLine = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: false,
  visInfo,
  render(api, spec, $dom): Partial<StandardChartLayerRenderResult> {
    const { data: dataArray, util, hook } = api;
    const presetOption = hook.buildStandardChartPresetOption(api);
    const {
      dataIndex = 0,
      encoding: { x, y: yTemp, z },
      dvBackgroundLayers = []
    } = spec;
    const { proxy } = api.spec;
    const y = Array.isArray(yTemp) ? yTemp : [yTemp];

    // * data ------------------------------------------------------------
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);
    const { x: xAxisData, z: zNameData } = util.dataEncoding.encoding(data.values.slice(), {
      x: {
        field: x,
        transforms: [{ type: 'sort', order: 'ascending' }, { type: 'deDuplication' }]
      },
      z: {
        field: z,
        transforms: [
          { type: 'deDuplication' },
          { type: 'filter', cb: val => typeof val !== 'undefined' }
        ]
      }
    });

    // * chart ------------------------------------------------------------
    const chartViewRect = $dom.getBoundingClientRect();
    const baseGridOption = Array.isArray(presetOption.grid)
      ? presetOption.grid[0]
      : presetOption.grid!;
    const grid = {
      ...baseGridOption
    };

    // 生成背景层markArea数据
    const markAreaData = generateMarkAreaData(dvBackgroundLayers, grid.top);

    const series = zNameData.map((zItem, index) => {
      const baseSeriesConfig = {
        type: 'line',
        name: String(zItem),
        xAxisIndex: 0,
        yAxisIndex: 0,
        __encodeField: String(zItem),
        data: util.dataTransform.transform(
          xAxisData.map(
            xItem =>
              data.values.find(dataItem => dataItem[x] === xItem && dataItem[z] === zItem)?.[
                y[0]
              ] ?? 0
          ),
          [{ type: 'toNumeric' }]
        ) as number[]
      };

      // 只在第一个系列添加markArea
      return index === 0
        ? {
            ...baseSeriesConfig,
            markArea: {
              silent: true,
              data: markAreaData
            }
          }
        : baseSeriesConfig;
    });

    const yAxis = [
      {
        show: true,
        __dataIndex: 0,
        __encodeField: y,
        type: 'value' as const,
        name: y[0] + (dataMetaInfo[y[0]]?.unit ? `(${dataMetaInfo[y[0]].unit})` : ''),
        dvSplitLineNumber: hook.getSplitLineNumber(
          chartViewRect.height - (grid.top || 0) - (grid.bottom || 0)
        )
      }
    ];

    const option: Partial<StandardChartLayerRenderResult> = {
      grid: [grid],
      xAxis: [
        {
          show: true,
          __dataIndex: dataIndex,
          __encodeField: x,
          type: 'category',
          name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
          data: xAxisData as string[],
          boundaryGap: false,
          axisTick: {
            show: false
          }
        }
      ],
      yAxis,
      series
    };

    // 添加tooltip配置
    Object.assign(option, {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line'
        }
      }
    });

    return proxy ? getRenderOption(option) : option;
  }
});

export { hierarchicalLine as layer };
